import React, { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import {
  getPurchaseLines,
  pricePurchaseLine,
  approvePurchaseLines,
  rejectPurchaseLine,
  getPurchaseVendors,
  downloadPurchaseOrderPdf,
} from "../../api/service";
import { AgeingSelect, ProjectFilter, RemarksPanel, fmtMoney, fmtQty } from "./shared";

const RUPEE = "\u20B9";
const PAGE_SIZE = 50;
const CHIPS = [
  { id: "pending", label: "Awaiting pricing" },
  { id: "priced", label: "Awaiting approval" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];
const CATEGORY = {
  cleared: { label: "System cleared", cls: "bg-success" },
  exception: { label: "Exception", cls: "bg-warning text-dark" },
  awaiting_payment: { label: "Awaiting payment", cls: "bg-secondary" },
};

function deltaClass(v) {
  const n = Number(v);
  if (v === null || v === undefined || Number.isNaN(n) || n === 0) return "";
  return n > 0 ? "text-danger" : "text-success";
}

function signed(v) {
  if (v === null || v === undefined) return "-";
  return (Number(v) > 0 ? "+" : "") + fmtMoney(v);
}

function baseDraft(line) {
  return {
    price: line.unit_price || "",
    vendor: line.vendor || null,
    input: line.vendor ? line.vendor.name : "",
    exact: null,
    reason: line.exception_reason || "",
  };
}

function VendorCell({ draft, onChange }) {
  const [options, setOptions] = useState([]);
  const [active, setActive] = useState(false);
  const typed = draft.input.trim();

  useEffect(() => {
    if (!active) return undefined;
    let live = true;
    const h = setTimeout(async () => {
      try {
        const res = await getPurchaseVendors({ q: typed });
        if (live) setOptions(res?.results || []);
      } catch {
        if (live) setOptions([]);
      }
    }, 250);
    return () => {
      live = false;
      clearTimeout(h);
    };
  }, [typed, active]);

  const isKnown =
    (draft.vendor && draft.vendor.name && draft.vendor.name.trim().toLowerCase() === typed.toLowerCase()) ||
    !!draft.exact;

  return (
    <div style={{ minWidth: 200 }}>
      <Autocomplete
        freeSolo
        size="small"
        options={options}
        value={draft.vendor}
        onOpen={() => setActive(true)}
        getOptionLabel={(o) => (typeof o === "string" ? o : o.name || "")}
        filterOptions={(x) => x}
        inputValue={draft.input}
        onInputChange={(_, v) =>
          onChange({
            input: v,
            exact: options.find((o) => o.name.trim().toLowerCase() === v.trim().toLowerCase()) || null,
          })
        }
        onChange={(_, v) => onChange({ vendor: v && typeof v !== "string" ? v : null })}
        renderOption={(props, o) => (
          <li {...props}>
            {o.name}
            {o.is_one_time ? " (one-time)" : ""}
          </li>
        )}
        renderInput={(params) => <TextField {...params} placeholder="Vendor name" />}
      />
      {typed && !isKnown && <div className="small text-muted">New one-time vendor</div>}
    </div>
  );
}

function RejectForm({ line, onSaved, onCancel }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (reason.trim().length < 3) {
      setErr("Give a rejection reason.");
      return;
    }
    if (!window.confirm("Reject this line? Rejection is final.")) return;
    setBusy(true);
    try {
      await rejectPurchaseLine(line.id, { reason: reason.trim() });
      onSaved();
    } catch (e) {
      setErr(e.message || "Could not reject the line.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-2 bg-light border rounded">
      {err && <div className="alert alert-danger py-1">{err}</div>}
      <div className="d-flex gap-2 align-items-center">
        <input
          className="form-control form-control-sm"
          maxLength={500}
          placeholder="Reason for rejection (required, final)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={save}>
          Reject
        </button>
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function ApproveTab({ opts }) {
  const canPrice = !!opts.can_price;
  const canApprove = !!opts.can_approve;
  const showPrices = canPrice || canApprove;

  const [chip, setChip] = useState(canApprove ? "priced" : "pending");
  const [project, setProject] = useState(null);
  const [ageing, setAgeing] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ results: [], count: 0, status_counts: {} });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [open, setOpen] = useState(null);
  const [selected, setSelected] = useState({});
  const [note, setNote] = useState("");
  const [tick, setTick] = useState(0);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setLoading(true);
    const params = { status: chip, page: page, page_size: PAGE_SIZE };
    if (project) params.project = project.id;
    if (ageing) params.ageing = ageing;
    if (category) params.category = category;
    getPurchaseLines(params)
      .then((res) => {
        if (live) {
          setData(res);
          setErr("");
        }
      })
      .catch((e) => {
        if (live) setErr(e.message || "Could not load lines.");
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [chip, project, ageing, category, page, tick]);

  const reload = () => {
    setOpen(null);
    setSelected({});
    setTick((t) => t + 1);
  };
  const resetView = () => {
    setPage(1);
    setSelected({});
    setOpen(null);
  };

  const getDraft = (r) => drafts[r.id] || baseDraft(r);
  const setDraft = (r, patch) =>
    setDrafts((s) => ({ ...s, [r.id]: { ...(s[r.id] || baseDraft(r)), ...patch } }));

  const saveRow = async (r) => {
    const d = getDraft(r);
    const typed = d.input.trim();
    const price = parseFloat(d.price);
    const gated = !r.gate.met;
    const tag = r.request_number + " " + r.summary + ": ";
    if (!(price > 0)) {
      setErr(tag + "enter a price above 0.");
      return;
    }
    if (!typed) {
      setErr(tag + "choose or type a vendor.");
      return;
    }
    if (gated && d.reason.trim().length < 5) {
      setErr(tag + "payment gate not met - give an exception reason (at least 5 characters).");
      return;
    }
    const payload = { unit_price: d.price };
    const ex =
      d.vendor && d.vendor.name && d.vendor.name.trim().toLowerCase() === typed.toLowerCase() ? d.vendor : d.exact;
    if (ex) payload.vendor_id = ex.id;
    else payload.vendor_name = typed;
    if (gated) payload.exception_reason = d.reason.trim();
    setSavingId(r.id);
    setErr("");
    setMsg("");
    try {
      await pricePurchaseLine(r.id, payload);
      setDrafts((s) => {
        const n = { ...s };
        delete n[r.id];
        return n;
      });
      setMsg("Saved price for " + r.request_number + " - " + r.summary);
      reload();
    } catch (e) {
      setErr(tag + (e.message || "could not save the price."));
    } finally {
      setSavingId(null);
    }
  };

  const rows = data.results || [];
  const groupAdj = {};
  if (canPrice) {
    rows.forEach((x) => {
      if (!x.contrib || !(x.status === "pending" || x.status === "priced")) return;
      const dd = drafts[x.id];
      if (!dd) return;
      const lp = parseFloat(dd.price);
      if (!(lp > 0)) return;
      const q = Number(x.approved_qty ?? x.qty);
      const key = x.project_id + "|" + x.group;
      groupAdj[key] = (groupAdj[key] || 0) + (lp * q - Number(x.contrib.own_value || 0));
    });
  }
  const selectable = canApprove && chip === "priced";
  const selectedCount = Object.keys(selected).length;
  const selectableRows = rows.filter((r) => r.vendor && r.unit_price !== null);
  const allSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selected[r.id] !== undefined);
  const colSpan = (selectable ? 1 : 0) + 4 + (showPrices ? 7 : 0) + 2;
  const totalPages = Math.max(1, Math.ceil((data.count || 0) / PAGE_SIZE));

  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next = {};
    selectableRows.forEach((r) => {
      next[r.id] = r.qty;
    });
    setSelected(next);
  };
  const toggleOne = (r) =>
    setSelected((s) => {
      const n = { ...s };
      if (n[r.id] !== undefined) delete n[r.id];
      else n[r.id] = r.qty;
      return n;
    });
  const setQty = (r, v) => setSelected((s) => ({ ...s, [r.id]: v }));

  const approve = async () => {
    const chosen = rows.filter((r) => selected[r.id] !== undefined);
    if (!chosen.length) return;
    const bad = chosen.find((r) => !(parseFloat(selected[r.id]) > 0));
    if (bad) {
      setErr("Approved quantity must be above 0 (" + bad.request_number + ": " + bad.summary + ").");
      return;
    }
    const vendors = new Set(chosen.map((r) => r.vendor && r.vendor.id));
    if (
      !window.confirm(
        "Approve " + chosen.length + " line(s)? This creates " + vendors.size + " purchase order(s), one per vendor."
      )
    )
      return;
    setBusy(true);
    setErr("");
    try {
      const res = await approvePurchaseLines({
        items: chosen.map((r) => ({ id: r.id, approved_qty: selected[r.id] })),
        note: note,
      });
      setMsg(
        "Created " +
          res.orders
            .map((o) => o.number + " - " + o.vendor.name + " (" + RUPEE + fmtMoney(o.total) + ")")
            .join(", ")
      );
      setNote("");
      reload();
    } catch (e) {
      setErr(e.message || "Approval failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {CHIPS.map((c) => (
          <button
            key={c.id}
            type="button"
            className={"btn btn-sm " + (chip === c.id ? "btn-primary" : "btn-outline-primary")}
            onClick={() => {
              setChip(c.id);
              resetView();
              setMsg("");
            }}
          >
            {c.label} ({(data.status_counts || {})[c.id] || 0})
          </button>
        ))}
      </div>

      <div className="row g-2 align-items-end mb-3">
        <div className="col-auto">
          <label className="form-label small mb-1">Project</label>
          <ProjectFilter
            value={project}
            onChange={(v) => {
              setProject(v);
              resetView();
            }}
          />
        </div>
        <div className="col-auto">
          <label className="form-label small mb-1">Age (since submitted)</label>
          <AgeingSelect
            value={ageing}
            onChange={(v) => {
              setAgeing(v);
              resetView();
            }}
          />
        </div>
        <div className="col-auto">
          <label className="form-label small mb-1">Payment</label>
          <select
            className="form-select form-select-sm"
            value={category}
            onChange={(e) => {
              setCategory(e.target.value);
              resetView();
            }}
          >
            <option value="">All</option>
            <option value="cleared">System cleared</option>
            <option value="exception">Exception</option>
            <option value="awaiting_payment">Awaiting payment</option>
          </select>
        </div>
      </div>

      {err && <div className="alert alert-danger py-2">{err}</div>}
      {msg && <div className="alert alert-success py-2">{msg}</div>}

      {selectable && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={busy || selectedCount === 0}
            onClick={approve}
          >
            {busy ? "Approving..." : "Approve selected (" + selectedCount + ")"}
          </button>
          <input
            className="form-control form-control-sm"
            style={{ maxWidth: 320 }}
            maxLength={500}
            placeholder="Approval note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <span className="small text-muted">
            One PO is created per vendor. You can change the approved quantity before approving.
          </span>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-sm table-hover align-middle" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              {selectable && (
                <th>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
              )}
              <th>Request</th>
              <th>Project</th>
              <th>Item</th>
              <th>Qty</th>
              {showPrices && (
                <>
                  <th className="text-end">Rate card</th>
                  <th className="text-end">Price</th>
                  <th className="text-end">Delta / unit</th>
                  <th className="text-end">Delta total</th>
                  <th>Vendor</th>
                  <th className="text-end">Approved so far (amount &amp; %)</th>
                  <th className="text-end" style={{ background: "#fff3cd" }}>Total if all approved (amount &amp; %)</th>
                </>
              )}
              <th>Payment</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const cat = CATEGORY[r.category] || CATEGORY.cleared;
              const actionable = r.status === "pending" || r.status === "priced";
              const editing = canPrice && actionable;
              const d = editing ? getDraft(r) : null;
              const livePrice = d ? parseFloat(d.price) : NaN;
              const refNum = r.ref_rate !== null && r.ref_rate !== undefined ? Number(r.ref_rate) : null;
              const liveU = editing && refNum !== null && livePrice > 0 ? livePrice - refNum : null;
              const dpu = liveU !== null ? liveU : r.delta_per_unit;
              const dtot = liveU !== null ? liveU * Number(r.approved_qty ?? r.qty) : r.delta_total;
              const dpct = liveU !== null && refNum ? Math.round((liveU / refNum) * 1000) / 10 : r.delta_pct;
              const cb = showPrices && r.contrib ? r.contrib : null;
              let pastNode = <span className="text-muted">-</span>;
              let afterNode = <span className="text-muted">-</span>;
              if (cb) {
                const den = cb.denominator ? Number(cb.denominator) : null;
                const past = Number(cb.approved_cost || 0);
                const total = Number(cb.group_total || 0) + (groupAdj[r.project_id + "|" + r.group] || 0);
                const pct = (v) => (den ? ((v / den) * 100).toFixed(1) + "%" : "n/a");
                const grp = String(cb.group || "").replace(/_/g, " ");
                pastNode = (
                  <div>
                    <div>
                      {RUPEE}
                      {fmtMoney(past)}
                    </div>
                    <div className="small text-muted">{pct(past)}</div>
                  </div>
                );
                afterNode =
                  total > 0 ? (
                    <div>
                      <div className="fw-bold">
                        {RUPEE}
                        {fmtMoney(total)}
                      </div>
                      <div className="fw-bold">{pct(total)}</div>
                      <div className="small text-muted" style={{ textTransform: "capitalize" }}>
                        {grp}: approved + all open lines
                      </div>
                      {cb.schedule_mismatch && (
                        <span
                          className="badge bg-light text-dark border"
                          title="Payment schedule total is more than 10% away from the quotation value"
                        >
                          schedule {"\u2260"} quote
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted small">Enter price</span>
                  );
              }
              return (
                <React.Fragment key={r.id}>
                  <tr>
                    {selectable && (
                      <td>
                        <input
                          type="checkbox"
                          disabled={!r.vendor}
                          checked={selected[r.id] !== undefined}
                          onChange={() => toggleOne(r)}
                        />
                      </td>
                    )}
                    <td>
                      {r.request_number}
                      <div className="small text-muted">{r.age_days}d old</div>
                    </td>
                    <td>{r.project_label}</td>
                    <td>
                      {r.summary}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {selectable && selected[r.id] !== undefined ? (
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control form-control-sm"
                          style={{ width: 90, display: "inline-block" }}
                          value={selected[r.id]}
                          onChange={(e) => setQty(r, e.target.value)}
                        />
                      ) : (
                        fmtQty(r.approved_qty ?? r.qty)
                      )}{" "}
                      {r.unit}
                      {r.approved_qty && Number(r.approved_qty) !== Number(r.qty) && (
                        <div className="small text-muted">requested {fmtQty(r.qty)}</div>
                      )}
                    </td>
                    {showPrices && (
                      <>
                        <td className="text-end">
                          {r.ref_rate ? fmtMoney(r.ref_rate) : <span className="text-muted">no rate</span>}
                        </td>
                        <td className="text-end" style={{ minWidth: 110 }}>
                          {editing ? (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="form-control form-control-sm text-end"
                              style={{ width: 100, display: "inline-block" }}
                              placeholder={"per " + r.unit}
                              title={"Price per " + r.unit}
                              value={d.price}
                              onChange={(e) => setDraft(r, { price: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveRow(r);
                              }}
                            />
                          ) : r.unit_price ? (
                            fmtMoney(r.unit_price)
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className={"text-end " + deltaClass(dpu)}>
                          {dpu !== null && dpu !== undefined ? signed(dpu) : "-"}
                        </td>
                        <td className={"text-end " + deltaClass(dtot)}>
                          {dtot !== null && dtot !== undefined ? signed(dtot) : "-"}
                          {dpct !== null && dpct !== undefined && (
                            <div className="small">
                              {dpct > 0 ? "+" : ""}
                              {dpct}%
                            </div>
                          )}
                        </td>
                        <td>
                          {editing ? (
                            <VendorCell draft={d} onChange={(patch) => setDraft(r, patch)} />
                          ) : r.vendor ? (
                            r.vendor.name + (r.vendor.is_one_time ? " (one-time)" : "")
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="text-end">{pastNode}</td>
                        <td className="text-end" style={{ background: "#fff3cd" }}>{afterNode}</td>
                      </>
                    )}
                    <td>
                      <span className={"badge " + cat.cls}>{cat.label}</span>
                      {editing && !r.gate.met && (
                        <input
                          className="form-control form-control-sm mt-1"
                          maxLength={500}
                          placeholder="Exception reason (required)"
                          value={d.reason}
                          onChange={(e) => setDraft(r, { reason: e.target.value })}
                        />
                      )}
                      {r.category === "awaiting_payment" && r.gate.reason && (
                        <div className="small text-muted">{r.gate.reason}</div>
                      )}
                      {r.exception_reason && (
                        <div className="small text-muted" title={r.exception_reason}>
                          {r.exception_reason.length > 40
                            ? r.exception_reason.slice(0, 40) + "..."
                            : r.exception_reason}
                        </div>
                      )}
                    </td>
                    <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                      {editing && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm me-1"
                          disabled={
                            savingId === r.id ||
                            !(livePrice > 0) ||
                            !d.input.trim() ||
                            (!r.gate.met && d.reason.trim().length < 5)
                          }
                          onClick={() => saveRow(r)}
                        >
                          {savingId === r.id ? "Saving..." : r.status === "pending" ? "Save" : "Update"}
                        </button>
                      )}
                      {canApprove && actionable && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm me-1"
                          onClick={() => setOpen({ id: r.id, mode: "reject" })}
                        >
                          Reject
                        </button>
                      )}
                      {r.po && showPrices ? (
                        <button
                          type="button"
                          className="btn btn-info btn-sm me-1"
                          title="Download purchase order PDF"
                          onClick={() =>
                            downloadPurchaseOrderPdf(r.po.id, r.po.number).catch((e) =>
                              setErr(e.message || "Could not download the PO.")
                            )
                          }
                        >
                          {r.po.number} {"\u2B07"}
                        </button>
                      ) : (
                        r.po && <span className="badge bg-info text-dark me-1">{r.po.number}</span>
                      )}
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => setOpen({ id: r.id, mode: "remarks" })}
                      >
                        Remarks
                      </button>
                    </td>
                  </tr>
                  {r.status === "rejected" && r.decision_note && (
                    <tr>
                      <td colSpan={colSpan} className="small text-danger">
                        Rejected: {r.decision_note}
                      </td>
                    </tr>
                  )}
                  {open && open.id === r.id && (
                    <tr>
                      <td colSpan={colSpan}>
                        {open.mode === "reject" && (
                          <RejectForm line={r} onSaved={reload} onCancel={() => setOpen(null)} />
                        )}
                        {open.mode === "remarks" && <RemarksPanel requestId={r.request_id} />}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={colSpan} className="text-center text-muted py-3">
                  No lines here.
                </td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan={colSpan} className="text-center text-muted py-3">
                  Loading...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-flex align-items-center justify-content-between">
        <span className="small text-muted">{data.count || 0} line(s)</span>
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </button>
          <span className="small">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
